import React, { useEffect } from 'react'
import { Phone, Mail, MapPin, Clock, ShieldCheck } from 'lucide-react'
import ContactForm from '../components/ContactForm'

export default function ContactPage() {
  useEffect(() => {
    // Set SEO Meta
    document.title = 'Kontakt Craft Café | Bestil catering og bageri i Horsens'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Kontakt Craft Café på tlf. 45 48 99 81 eller craft.line25@gmail.com. Vi leverer catering, tapas, mad ud af huset, kager og brød i Horsens. Send forespørgsel.'
      )
    }
  }, [])

  return (
    <div className="pt-[70px] md:pt-[100px]">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#faf9f6] text-[#1c2b21] border-b border-stone-200">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/d0137351-6fb2-49e8-b9ff-9d5f7ea8fbce.jpg"
            alt="Kontakt Craft Café"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf9f6] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 py-20 md:py-28 text-center space-y-6">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#b89a6c] font-light">
            Kontakt os
          </span>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide leading-relaxed text-[#1c2b21]">
            Kontakt os – vi står klar til at hjælpe dig
          </h1>
          <p className="text-xs md:text-sm text-stone-600 max-w-2xl mx-auto leading-relaxed font-light uppercase tracking-wider">
            Ring, skriv eller besøg os på Vejlevej 10 i Horsens. Vi glæder os til at høre om dine ønsker til maden.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-6">
        <div className="grid gap-12 lg:grid-cols-12 items-start">
          {/* Info Column */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-3">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#b89a6c] font-light">Direkte kontakt</span>
              <h2 className="font-serif text-2xl md:text-3xl font-light text-[#1c2b21] tracking-wide">Lad os tage en snak om din næste fest</h2>
              <p className="text-xs text-stone-600 font-light leading-relaxed">
                Har du spørgsmål til vores menuer, ønsker du et skræddersyet tilbud, eller vil du afgive en bestilling? Du er altid mere end velkommen til at kontakte os. Vi sidder klar til at besvare dine spørgsmål og rådgive dig bedst muligt.
              </p>
            </div>

            {/* Contact Info Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {/* Phone Card */}
              <div className="flex items-start gap-4 border border-stone-200 bg-[#f4f1ea] p-5 rounded-none shadow-none">
                <div className="border border-[#b89a6c]/20 p-2.5 text-[#b89a6c]">
                  <Phone size={16} />
                </div>
                <div>
                  <h4 className="font-serif text-sm font-light text-[#1c2b21] tracking-wide">Ring direkte</h4>
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5 font-light">Mandag - fredag kl. 08-17</p>
                  <a href="tel:+45489981" className="mt-2 inline-block text-xs font-semibold uppercase tracking-widest text-[#b89a6c] hover:text-[#1c2b21] transition-colors whitespace-nowrap">
                    45 48 99 81
                  </a>
                </div>
              </div>

              {/* Email Card */}
              <div className="flex items-start gap-4 border border-stone-200 bg-[#f4f1ea] p-5 rounded-none shadow-none">
                <div className="border border-[#b89a6c]/20 p-2.5 text-[#b89a6c]">
                  <Mail size={16} />
                </div>
                <div>
                  <h4 className="font-serif text-sm font-light text-[#1c2b21] tracking-wide">Send en e-mail</h4>
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5 font-light">Vi svarer inden for 24 timer</p>
                  <a href="mailto:craft.line25@gmail.com" className="mt-2 inline-block text-xs font-semibold uppercase tracking-widest text-[#b89a6c] hover:text-[#1c2b21] transition-colors whitespace-nowrap">
                    craft.line25@gmail.com
                  </a>
                </div>
              </div>

              {/* Address Card */}
              <div className="flex items-start gap-4 border border-stone-200 bg-[#f4f1ea] p-5 rounded-none shadow-none">
                <div className="border border-[#b89a6c]/20 p-2.5 text-[#b89a6c]">
                  <MapPin size={16} />
                </div>
                <div>
                  <h4 className="font-serif text-sm font-light text-[#1c2b21] tracking-wide">Besøg os</h4>
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5 font-light">Vores professionelle køkken</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-stone-700">
                    Vejlevej 10, 8700 Horsens
                  </p>
                </div>
              </div>

              {/* Opening Hours Card */}
              <div className="flex items-start gap-4 border border-stone-200 bg-[#f4f1ea] p-5 rounded-none shadow-none">
                <div className="border border-[#b89a6c]/20 p-2.5 text-[#b89a6c]">
                  <Clock size={16} />
                </div>
                <div>
                  <h4 className="font-serif text-sm font-light text-[#1c2b21] tracking-wide">Bestilling & Levering</h4>
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5 font-light">Vi leverer mad alle ugens dage</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-stone-700">
                    Levering sker efter aftale, uanset ugedag!
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="flex items-center gap-3 border border-[#b89a6c]/20 bg-[#b89a6c]/5 p-4 text-[#b89a6c] rounded-none">
              <ShieldCheck size={20} className="shrink-0" />
              <p className="text-[11px] font-light leading-relaxed">
                Sikker og krypteret indsendelse af dine oplysninger. Vi deler aldrig dine data med tredjepart.
              </p>
            </div>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  )
}
